import { customElement, property } from "lit/decorators.js";
import { LitElementBase } from "../LitElementBase";
import { css, html, nothing, PropertyValues } from "lit";
import { inViewport } from "../../util/domutils";

@customElement("ehm-drawer")
export class Drawer extends LitElementBase {
  #menuButton: HTMLButtonElement | null = null;
  @property({ type: Boolean, reflect: true }) open = false;

  static styles = css`
    .drawer {
      margin: 0;
      padding: 0 0;
      border: none;
      border-radius: 0;
      position: absolute;
      max-width: var(--menu-width, 468px);
      width: 100%;
      right: 0;
      left: auto;
      color: var(--menu-fg, var(--text-white));
      z-index: 1000;
      background-color: var(--menu-bg, #fff);
      display: none;
      max-height: calc(100vh - var(--menu-offset));
      overflow-y: auto;
    }
    :host([open]) .drawer {
      display: block;
    }
    slot[name="close-button"] {
      position: absolute;
      right: var(--site-padding);
      top: 1rem;
      display: block;
    }
    :host([open]) .backdrop {
      opacity: 1;
    }
    .backdrop {
      position: fixed;
      opacity: 0;
      top: var(--menu-offset, 6rem);
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(3px);
      z-index: 999;
      transition: opacity 300ms ease-in-out;
    }
    @starting-style {
      :host([open]) .backdrop {
        opacity: 0;
      }
    }
  `;

  constructor() {
    super();
    this.#menuButton = document.querySelector(`button[aria-controls="${this.id}"]`);
    this.#menuButton?.addEventListener("click", () => (this.handleOpen()));
    this.#menuButton?.setAttribute("aria-expanded", "false");
  }

  firstUpdated() {
    this.shadowRoot?.querySelector(".drawer")?.addEventListener("click", (e) => {
      e.stopPropagation();
    });
  }

  updated(changedProperties: PropertyValues) {
    const prevOpen = changedProperties.get("open");
    const params = new URLSearchParams(window.location.search);

    if (prevOpen === undefined) {
      if (params.get("nav") === "open") {
        this.open = true;
      }
      return;
    }

    if (prevOpen) {
      this.#closeDrawer(params);
    } else {
      this.#openDrawer(params);
    }

    window.history.replaceState(null, "", `?${params.toString()}`);
  }

  handleOpen() {
    this.open = !this.open
      if (document.querySelector(".vma")) {
        const vmas = document.querySelectorAll(".vma") as NodeListOf<HTMLElement>;
        let totalVmaHeight = 0;
        vmas.forEach(vma => {
          totalVmaHeight += inViewport(vma);
        })
        totalVmaHeight += (document.querySelector("header") as HTMLElement).offsetHeight;
        (this.shadowRoot?.querySelector(".backdrop") as HTMLElement).style.top = totalVmaHeight + "px";
        (this.shadowRoot?.querySelector(".drawer") as HTMLElement).style.maxHeight = `calc(100vh - ${totalVmaHeight}px)`;
      }
  }

  #openDrawer(params: URLSearchParams) {
    params.set("nav", "open");
    this.#setMenuButtonState(true);
    this.#toggleInertElements(true);
    this.#addEventListeners();
  }

  #closeDrawer(params: URLSearchParams) {
    params.delete("nav");
    this.#setMenuButtonState(false);
    this.#toggleInertElements(false);
    this.#removeEventListeners();
  }

  #setMenuButtonState(expanded: boolean) {
    this.#menuButton?.setAttribute("aria-expanded", expanded.toString());
  }

  #toggleInertElements(inert: boolean) {
    const elements = document.querySelectorAll(".inert-on-nav");
    elements.forEach((el) => {
      if (inert) {
        el.setAttribute("inert", "");
      } else {
        el.removeAttribute("inert");
      }
    });
  }

  #addEventListeners() {
    setTimeout(() => {
      document.body.addEventListener("click", this.#handleBackdropClick);
      document.addEventListener("keydown", this.#handleEscKey);
    }, 100);
  }

  #removeEventListeners() {
    document.body.removeEventListener("click", this.#handleBackdropClick);
    document.removeEventListener("keydown", this.#handleEscKey);
  }

  #handleBackdropClick = () => {
    this.open = false;
  };

  #handleEscKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      this.open = false;
    }
  };

  render() {
    return html`
      <div class="drawer" part="dialog">
        <slot name="close-button" @click=${this.handleOpen}></slot>
        <slot></slot>
      </div>
      <div class="backdrop ${this.open ? "" : "hidden"}" part="backdrop"></div>
    `;
  }
}
